import { useState, useEffect } from 'react';
import { useGetRewardsQuery } from '~/generated/hooks';

interface RewardName {
  id: string;
  name: string;
}

/**
 * Hook to resolve reward IDs to names
 * @param rewardIds Array of reward IDs to resolve
 * @returns Map of reward ID to reward name
 */
export const useRewardNames = (rewardIds: string[]) => {
  const [rewardNames, setRewardNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { refetch } = useGetRewardsQuery(
    {
      input: {
        search: '',
      },
    },
    {
      enabled: false, // Don't run automatically
      onSuccess: (data) => {
        const nameMap = new Map<string, string>();
        data?.getRewards.items.forEach((reward) => {
          if (rewardIds.includes(reward.id)) {
            nameMap.set(reward.id, reward.name);
          }
        });
        setRewardNames(nameMap);
        setError(null);
        setLoading(false);
      },
      onError: (error) => {
        // Only log errors in development mode
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch reward names:', error);
        }
        setError(error instanceof Error ? error : new Error('Unknown error occurred'));
        setLoading(false);
      },
    }
  );

  useEffect(() => {
    if (rewardIds.length > 0) {
      setLoading(true);
      setError(null);
      refetch();
    }
  }, [rewardIds.join(','), refetch]);

  const getRewardName = (rewardId: string): string => {
    return rewardNames.get(rewardId) || rewardId;
  };

  return {
    getRewardName,
    loading,
    error,
    rewardNames,
  };
};